import { useEffect, useState } from "react";
import { useRouter } from "../router";
import { EmbedLauncher } from "./quantfolioEmbed/EmbedLauncher";
import { EmbedSession } from "./quantfolioEmbed/EmbedSession";
import type { EmbedSession as Session } from "./quantfolioEmbed/embedApi";

const BASE_PATH = "/app/embedded-flow";

function readPositiveInt(param: string): number | null {
  if (typeof window === "undefined") return null;
  const n = Number(new URLSearchParams(window.location.search).get(param));
  return Number.isInteger(n) && n > 0 ? n : null;
}

// The "Embedded Flow" area (M13): the advisor runs a QP flow session inside
// Andersen, in an iframe, signed in through Andersen's IdP. Launcher vs.
// session view is driven by `?session={id}` (deep-linkable, back button works);
// `?investor={id}` preselects the investor in the launcher. See
// docs/quantfolio-embed.md.
export function QuantfolioEmbed() {
  const { navigate } = useRouter();
  const [sessionId, setSessionId] = useState<number | null>(() => readPositiveInt("session"));
  // The session returned by "open" — saves the view a round trip on first paint.
  const [opened, setOpened] = useState<Session | null>(null);

  // The router tracks the path only; a query-string change (back/forward
  // between launcher and session) has to be picked up here.
  useEffect(() => {
    const onPop = () => {
      setSessionId(readPositiveInt("session"));
      setOpened(null);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  function open(session: Session) {
    setOpened(session);
    setSessionId(session.id);
    navigate(`${BASE_PATH}?session=${session.id}`);
  }

  function exit() {
    setOpened(null);
    setSessionId(null);
    navigate(BASE_PATH);
  }

  if (sessionId !== null) {
    return (
      <EmbedSession
        key={sessionId}
        sessionId={sessionId}
        initial={opened?.id === sessionId ? opened : null}
        onExit={exit}
      />
    );
  }
  return <EmbedLauncher initialInvestorId={readPositiveInt("investor")} onOpen={open} />;
}
